/**
 * 'firebase/firestore' with permission-denied telemetry (2026-10-16).
 *
 * craco.config.js aliases the bare specifier `firebase/firestore` to this file
 * for the WEB BUNDLE ONLY (not jest, not tsc — types are unchanged because
 * every override keeps the SDK's own signature). Everything is re-exported
 * from '@firebase/firestore' (which is all `firebase/firestore` itself does);
 * the read/write entry points below are thin wrappers that report a
 * `permission-denied` with its real path and rethrow / forward it untouched.
 *
 * Kill switch: build with HRX_FIRESTORE_TELEMETRY=off to drop the alias.
 * ☠️ Never import 'firebase/firestore' from here or from permissionTelemetry/
 * (that would resolve back to this file).
 */
import * as fs from '@firebase/firestore';

import { reportFirestorePermissionError, wrapSnapshotArgs, type FirestoreOp } from './permissionTelemetry';

export * from '@firebase/firestore';

type AnyFn = (...args: any[]) => any;

function trackPromise<F extends AnyFn>(fn: F, op: FirestoreOp): F {
  const wrapped = function (this: unknown, ...args: any[]) {
    const target = args[0];
    const out = fn.apply(this, args);
    if (out && typeof out.then === 'function') {
      return out.then(undefined, (err: unknown) => {
        reportFirestorePermissionError(err, target, op);
        throw err;
      });
    }
    return out;
  };
  return wrapped as unknown as F;
}

export const getDoc: typeof fs.getDoc = trackPromise(fs.getDoc, 'get');
export const getDocFromServer: typeof fs.getDocFromServer = trackPromise(fs.getDocFromServer, 'get');
export const getDocs: typeof fs.getDocs = trackPromise(fs.getDocs, 'list');
export const getDocsFromServer: typeof fs.getDocsFromServer = trackPromise(fs.getDocsFromServer, 'list');
export const getCountFromServer: typeof fs.getCountFromServer = trackPromise(fs.getCountFromServer, 'list');
export const getAggregateFromServer: typeof fs.getAggregateFromServer = trackPromise(fs.getAggregateFromServer, 'list');
export const setDoc: typeof fs.setDoc = trackPromise(fs.setDoc, 'write');
export const updateDoc: typeof fs.updateDoc = trackPromise(fs.updateDoc, 'write');
export const deleteDoc: typeof fs.deleteDoc = trackPromise(fs.deleteDoc, 'write');
export const addDoc: typeof fs.addDoc = trackPromise(fs.addDoc, 'write');

export const onSnapshot: typeof fs.onSnapshot = function (this: unknown, ...args: any[]) {
  const [target, ...rest] = args;
  let wrappedRest: unknown[] = rest;
  try {
    wrappedRest = wrapSnapshotArgs(rest, (err) => reportFirestorePermissionError(err, target, 'listen'));
  } catch {
    wrappedRest = rest;
  }
  return (fs.onSnapshot as AnyFn).apply(this, [target, ...wrappedRest]);
} as typeof fs.onSnapshot;
